import type { StudentSubscription } from '../types/subscription';

function toDateOnly(date: Date): string {
  return date.toISOString().split('T')[0];
}

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function parseDateOnly(value: string | null | undefined): Date | null {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) {
    return null;
  }

  const parsed = new Date(`${trimmed}T00:00:00.000Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function parsePlanTransitionStartDate(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!DATE_ONLY_PATTERN.test(trimmed)) {
    return null;
  }

  const parsed = parseDateOnly(trimmed);
  if (!parsed || toDateOnly(parsed) !== trimmed) {
    return null;
  }

  return trimmed;
}

function addUtcDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

function addUtcMonths(date: Date, months: number): Date {
  const next = new Date(date);
  next.setUTCMonth(next.getUTCMonth() + months);
  return next;
}

/** Months of coverage for non-monthly cycles. Null = monthly/free loyalty. */
export function expectedLoyaltyMonthsForBillingCycle(billingCycle: string): number | null {
  switch (String(billingCycle).trim().toLowerCase()) {
    case 'bimonthly':
    case 'bimestral':
      return 2;
    case 'quarterly':
    case 'trimestral':
      return 3;
    case 'semestral':
    case 'semiannually':
      return 6;
    case 'yearly':
    case 'annual':
    case 'anual':
      return 12;
    default:
      return null;
  }
}

export function addBillingCycleDate(startDate: string, billingCycle: string): string {
  const parsed = parseDateOnly(startDate);
  if (!parsed) {
    return startDate;
  }

  const normalizedCycle = String(billingCycle).trim().toLowerCase();
  let next = new Date(parsed);

  switch (normalizedCycle) {
    case 'weekly':
      next = addUtcDays(parsed, 7);
      break;
    case 'biweekly':
      next = addUtcDays(parsed, 14);
      break;
    case 'bimonthly':
      next = addUtcMonths(parsed, 2);
      break;
    case 'quarterly':
      next = addUtcMonths(parsed, 3);
      break;
    case 'semestral':
    case 'semiannually':
      next = addUtcMonths(parsed, 6);
      break;
    case 'yearly':
    case 'annual':
      next = addUtcMonths(parsed, 12);
      break;
    default:
      next = addUtcMonths(parsed, 1);
      break;
  }

  return toDateOnly(next);
}

export function isStudentSubscriptionCanceled(
  subscription: Pick<StudentSubscription, 'canceled_at'> | null | undefined,
): boolean {
  return Boolean(subscription?.canceled_at);
}

export function isStudentSubscriptionEnded(
  subscription: Pick<StudentSubscription, 'ended_at'> | null | undefined,
): boolean {
  return Boolean(subscription?.ended_at);
}

export function isStudentSubscriptionActive(
  subscription: Pick<StudentSubscription, 'canceled_at' | 'ended_at'> | null | undefined,
): boolean {
  if (!subscription) {
    return false;
  }

  return !subscription.canceled_at && !subscription.ended_at;
}

function isChargeableStudentSubscription(
  subscription: StudentSubscription & {
    payment_system?: string | null;
    has_paid_charge?: boolean | null;
  },
): boolean {
  if (subscription.external_subscription_id?.trim()) {
    return true;
  }
  if (subscription.payment_system === 'pix') {
    return true;
  }
  return subscription.has_paid_charge === true;
}

export function pickDisplayStudentSubscription(
  subscriptions: StudentSubscription[],
): StudentSubscription | null {
  if (subscriptions.length === 0) {
    return null;
  }

  const activeSubscriptions = subscriptions
    .filter((item) => isStudentSubscriptionActive(item))
    .sort((left, right) => left.id - right.id);
  const chargeableSubscriptions = activeSubscriptions.filter((item) => (
    isChargeableStudentSubscription(item)
  ));

  if (chargeableSubscriptions[0]) {
    return chargeableSubscriptions[0];
  }

  if (activeSubscriptions[0]) {
    return activeSubscriptions[0];
  }

  const endedSubscriptions = subscriptions
    .filter((item) => isStudentSubscriptionEnded(item))
    .sort((left, right) => Date.parse(right.ended_at ?? '') - Date.parse(left.ended_at ?? ''));

  if (endedSubscriptions[0]) {
    return endedSubscriptions[0];
  }

  const canceledSubscriptions = subscriptions
    .filter((item) => isStudentSubscriptionCanceled(item))
    .sort((left, right) => Date.parse(right.canceled_at ?? '') - Date.parse(left.canceled_at ?? ''));

  return canceledSubscriptions[0] ?? null;
}

export function resolvePlanTransitionEffectiveStartDate(
  subscription: Pick<
    StudentSubscription,
    'start_date' | 'billing_cycle' | 'loyalty_months' | 'is_single_payment' | 'canceled_at' | 'ended_at'
  > | null | undefined,
  now = new Date(),
): string {
  const today = toDateOnly(now);
  if (!subscription || isStudentSubscriptionCanceled(subscription) || isStudentSubscriptionEnded(subscription)) {
    return today;
  }

  const startDate = typeof subscription.start_date === 'string' ? subscription.start_date : '';
  const parsedStartDate = parseDateOnly(startDate);
  if (!parsedStartDate) {
    return today;
  }

  if (subscription.is_single_payment) {
    const loyaltyMonths = Math.max(Number(subscription.loyalty_months) || 0, 1);
    const coveredUntil = toDateOnly(addUtcMonths(parsedStartDate, loyaltyMonths));
    return coveredUntil <= today ? today : coveredUntil;
  }

  let candidate = startDate;
  while (candidate <= today) {
    const next = addBillingCycleDate(candidate, subscription.billing_cycle ?? 'monthly');
    if (next === candidate) {
      return today;
    }
    candidate = next;
  }

  return candidate;
}
